import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '@/features/auth/api/authApi';
import { onAuthChange, setAccessToken } from '@/shared/api/httpClient';
import { exhibitorsApi } from '@/shared/api/exhibitorsApi';
import { cacheRepository } from '@/db/repositories/cacheRepository';
import type { AuthResponse, UserRole } from '@/shared/types/domain';

interface AuthContextValue {
  /** Undefined while the initial silent-refresh check is in flight. */
  user: AuthResponse | null | undefined;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberDevice: boolean) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Refreshes the offline exhibitor-context cache (crucially, eventId -- see
 * Stage 8 architectural notes) whenever a valid session for an exhibitor-role
 * user is established. This only ever runs while genuinely online (login and
 * silent-refresh both require connectivity), so it's the one safe place to
 * do this network call -- callers elsewhere (e.g. scan capture) must never
 * fetch this themselves, only read the cache.
 */
async function refreshExhibitorContextCache(auth: AuthResponse | null): Promise<void> {
  if (!auth || !auth.exhibitorId || (auth.role !== 'ExhibitorAdmin' && auth.role !== 'ExhibitorStaff')) {
    return;
  }

  try {
    const exhibitor = await exhibitorsApi.getById(auth.exhibitorId);
    await cacheRepository.setExhibitorContext({
      exhibitorId: exhibitor.id,
      eventId: exhibitor.eventId,
      companyName: exhibitor.companyName,
      brandColorHex: exhibitor.brandColorHex
    });
  } catch {
    // Non-fatal: if this fails (e.g. request races a connectivity drop),
    // scan capture simply falls back to whatever was cached from a previous
    // successful login -- see scanCaptureService for that fallback behaviour.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthResponse | null | undefined>(undefined);

  // Keep local state in sync with the httpClient's token store -- the
  // httpClient itself updates the token silently on background 401-refresh,
  // and this subscription is what propagates that into the React tree so
  // e.g. a session that quietly expired mid-scan-session is reflected in
  // the UI without a manual refetch.
  useEffect(
    () =>
      onAuthChange((auth) => {
        setUser(auth);
        void refreshExhibitorContextCache(auth);
      }),
    []
  );

  // On first load (page refresh, PWA relaunch), the access token is gone
  // from memory but the httpOnly refresh cookie may still be valid --
  // attempt a silent refresh before deciding the user is logged out.
  useEffect(() => {
    authApi
      .refresh()
      .then((auth) => setAccessToken(auth))
      .catch(() => setAccessToken(null));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      login: async (email, password, rememberDevice) => {
        const auth = await authApi.login(email, password, rememberDevice);
        setAccessToken(auth);
        return auth;
      },
      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          setAccessToken(null);
          // Pending (unsynced) scans are deliberately NOT cleared here --
          // each one already carries its own correct exhibitorId/eventId
          // captured at scan time (see scanCaptureService), so they remain
          // valid and will still sync regardless of who's logged in next.
          // Only the "current session's" context pointer is cleared, so a
          // different exhibitor logging in on a shared/kiosk device doesn't
          // have stale eventId/exhibitorId served to their new scans.
          await cacheRepository.clear();
        }
      },
      hasRole: (...roles: UserRole[]) => !!user && roles.includes(user.role)
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}